import React, { useCallback, useState } from "react";
import { Box, CircularProgress, Drawer, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Close, OpenInFull } from "@mui/icons-material";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";
import { clampSidebarWidth, useChat, useEnsureConversation } from "./ChatProvider";
import ConversationList from "./ConversationList";
import MessagePane from "./MessagePane";
import ThreadPanel from "./ThreadPanel";
import { CHAT_URL, buildChatUrl } from "./chatRoutes";
import { useChatDirectory } from "./chatUtils";
import { useChatDialogs } from "./useChatDialogs";

// Memoized so resize drags (which re-render the shell) don't re-render every message.
const SidebarContent = React.memo(function SidebarContent() {
  const navigate = useNavigate();
  const directory = useChatDirectory();
  const { sidebar, updateSidebar, conversations } = useChat();
  const { conversationId, threadId } = sidebar;
  const { conversation: active } = useEnsureConversation(conversationId, () =>
    updateSidebar({ conversationId: null, threadId: null })
  );

  const showList = useCallback(
    () => updateSidebar({ conversationId: null, threadId: null }),
    [updateSidebar]
  );
  const openThread = useCallback((messageId: number) => updateSidebar({ threadId: messageId }), [updateSidebar]);

  const { openNewChat, openNewChannel, openBrowseChannels, dialogs } = useChatDialogs((conversation) =>
    updateSidebar({ conversationId: conversation.id, threadId: null })
  );

  const actions = (
    <>
      <Tooltip title="Open full chat">
        <IconButton
          size="small"
          aria-label="Open full chat"
          onClick={() => {
            updateSidebar({ open: false });
            navigate(buildChatUrl(conversationId, threadId));
          }}
        >
          <OpenInFull sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Close chat">
        <IconButton size="small" aria-label="Close chat" onClick={() => updateSidebar({ open: false })}>
          <Close fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  let content: React.ReactNode;
  if (active && threadId) {
    content = (
      <ThreadPanel
        conversation={active}
        directory={directory}
        threadId={threadId}
        onClose={() => updateSidebar({ threadId: null })}
        backMode
        extraActions={actions}
      />
    );
  } else if (active) {
    content = (
      <MessagePane
        conversation={active}
        directory={directory}
        activeThreadId={null}
        onOpenThread={openThread}
        onLeft={showList}
        onBack={showList}
        reserveFabSpace
        extraActions={actions}
      />
    );
  } else if (conversationId) {
    content = (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <CircularProgress size={24} />
      </Box>
    );
  } else {
    content = (
      <ConversationList
        conversations={conversations}
        activeId={null}
        meId={directory.me.id}
        usersById={directory.usersById}
        onSelect={(conversation) => updateSidebar({ conversationId: conversation.id, threadId: null })}
        onNewChat={openNewChat}
        onNewChannel={openNewChannel}
        onBrowseChannels={openBrowseChannels}
        extraActions={actions}
      />
    );
  }

  return (
    <>
      {content}
      {dialogs}
    </>
  );
});

/** Docked on desktop (drag the left edge to resize); slide-over drawer on narrow screens. */
const ChatSidebar: React.FC<{ suppressed?: boolean }> = ({ suppressed }) => {
  const location = useLocation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const { enabled, sidebar, updateSidebar } = useChat();
  const onChatPage = Boolean(matchRoutes([{ path: `${CHAT_URL}/*` }], location.pathname));
  const [dragWidth, setDragWidth] = useState<number | null>(null);

  const startResize = (event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = sidebar.width;
    let width = startWidth;
    setDragWidth(width);
    const onMove = (e: MouseEvent) => {
      width = clampSidebarWidth(startWidth + (startX - e.clientX));
      setDragWidth(width);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      updateSidebar({ width });
      setDragWidth(null);
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  if (!enabled || suppressed || onChatPage) return null;

  if (isNarrow) {
    return (
      <Drawer
        anchor="right"
        open={sidebar.open}
        onClose={() => updateSidebar({ open: false })}
        slotProps={{ paper: { sx: { width: "min(100vw, 420px)" } } }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
          {sidebar.open && <SidebarContent />}
        </Box>
      </Drawer>
    );
  }

  if (!sidebar.open) return null;

  return (
    <Box
      component="aside"
      aria-label="Chat"
      sx={{
        position: "relative",
        width: dragWidth ?? sidebar.width,
        flexShrink: 0,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        borderLeft: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: "-2px 0 6px rgba(0,0,0,0.06)",
        "& > *:not(.chat-sidebar-resize)": { flex: 1, minHeight: 0 },
      }}
    >
      <Box
        className="chat-sidebar-resize"
        onMouseDown={startResize}
        sx={{
          position: "absolute",
          left: -3,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: "col-resize",
          zIndex: 5,
          "&:hover": { bgcolor: "primary.light", opacity: 0.4 },
        }}
      />
      <SidebarContent />
    </Box>
  );
};

export default ChatSidebar;
